import { useState } from 'react'

import { listOrphanAttachments, purgeOrphanAttachments } from '../../api/client'
import type { OrphanFile } from '../../api/client'

/** 字节数给人看 */
function humanSize(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(2)} MB`
}

/**
 * 附件清理（backlog #10：孤儿附件扫描）。
 *
 * 「孤儿」= 磁盘上有文件、但 Attachment 表里没有登记 —— 通常是删除条目/列/看板时
 * 进程被杀、或手工往目录里塞文件造成的。
 *
 * 这里只做两件事：扫描列出来、一键删掉。不做自动清理（误删风险太高）。
 */
export default function OrphanAttachments() {
  const [files, setFiles] = useState<OrphanFile[] | null>(null)
  const [dir, setDir] = useState('')
  const [bytes, setBytes] = useState(0)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')

  const scan = async () => {
    setBusy(true)
    setNotice('')
    try {
      const res = await listOrphanAttachments()
      setFiles(res.files)
      setDir(res.dir)
      setBytes(res.bytes)
      setNotice(res.count === 0 ? '没有孤儿文件' : `发现 ${res.count} 个孤儿文件`)
    } catch (e) {
      setNotice(`扫描失败：${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }

  const purge = async () => {
    if (files === null || files.length === 0) return
    setBusy(true)
    setNotice('')
    try {
      const res = await purgeOrphanAttachments()
      setFiles(res.files)
      setBytes(res.bytes)
      setNotice(
        `已删除 ${res.removedFiles} 个文件、${res.removedDirs} 个空目录` + (res.count > 0 ? `，仍有 ${res.count} 个删不掉` : ''),
      )
    } catch (e) {
      setNotice(`清理失败：${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card">
      <h2 className="card__title">附件清理</h2>
      <p className="card__hint">
        扫描存储目录里「有文件、但数据库没有登记」的孤儿附件（通常是删除条目/列/看板时进程被中断留下的）。
        默认只报告，不自动删除。
      </p>

      <div className="import-row">
        <button type="button" className="dialog__btn" onClick={() => void scan()} disabled={busy}>
          扫描
        </button>
        {files !== null && files.length > 0 && (
          <button type="button" className="dialog__btn" onClick={() => void purge()} disabled={busy}>
            删除这 {files.length} 个（{humanSize(bytes)}）
          </button>
        )}
        {dir !== '' && <span className="import-file">{dir}</span>}
      </div>

      {notice !== '' && <p className="card__hint">{notice}</p>}

      {files !== null && files.length > 0 && (
        <ul className="orphan-list">
          {files.map((f) => (
            <li key={f.storageName} className="orphan-row">
              <span className="orphan-row__name">{f.storageName}</span>
              <span className="orphan-row__size">{f.kind === 'dir' ? '空目录' : humanSize(f.sizeBytes)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
